import { useCallback, useMemo } from 'react';

import {
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/** An Azure AD security group, as a DaaS project stores and displays it. */
export type AzureSecurityGroup = {
  id: string;
  name: string;
};

const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';
const GRAPH_GROUP_SCOPE = 'https://graph.microsoft.com/Group.Read.All';

const SECURITY_GROUP_FILTER =
  'mailEnabled eq false and securityEnabled eq true';

/**
 * Directory groups that are never meaningful DaaS project security groups:
 * license, location, VPN and app-assignment groups, plus the PIM shadow groups.
 */
const WELL_KNOWN_GROUP_PREFIXES = [
  'RG_AZ_PIM_',
  'RG_AZ_APP_',
  'RG_APP_',
  'RG_AZ_TT_',
  'RG_AZ_LOC_',
  'RG_VPN_',
  'SPO_LOCATION_',
  'PG_CONCUR_SSO',
  'RG_AZ_SLACK_USER_GROUP_',
  'RG_AZ_CLOUDSPEND_READERS',
];

/** True for a group whose name marks it as directory plumbing, not a team. */
export const isWellKnownGroupName = (name: string): boolean => {
  const upperName = name.toUpperCase();
  return WELL_KNOWN_GROUP_PREFIXES.some(prefix =>
    upperName.startsWith(prefix.toUpperCase()),
  );
};

/** Microsoft Graph escapes a single quote inside an OData string literal by doubling it. */
const escapeODataLiteral = (value: string) => value.replace(/'/g, "''");

/**
 * Projects a Microsoft Graph collection response onto the picker's group shape.
 *
 * The response is external input that is rendered straight into the form, so
 * the shape is checked rather than asserted: a non-array `value`, a non-object
 * entry or an entry without a usable string `id` is dropped instead of
 * producing a group whose `id` is `undefined`.
 */
const toGroups = (body: unknown): AzureSecurityGroup[] => {
  const value = (body as { value?: unknown } | null | undefined)?.value;
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap(entry => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const { id, displayName } = entry as {
      id?: unknown;
      displayName?: unknown;
    };
    if (typeof id !== 'string' || !id) {
      return [];
    }
    return [{ id, name: typeof displayName === 'string' ? displayName : '' }];
  });
};

export interface AzureSecurityGroupsApi {
  /** The security groups the signed-in user is a member of. */
  listOwnGroups(): Promise<AzureSecurityGroup[]>;
  /** Security groups whose display name starts with `match`. */
  searchGroups(match: string): Promise<AzureSecurityGroup[]>;
  /** Display names for the given group ids, keyed by id. Unresolvable ids are omitted. */
  resolveGroupNames(ids: string[]): Promise<Map<string, string>>;
}

/**
 * Microsoft Graph access for Azure AD security groups.
 *
 * Talks to Graph through `fetchApi` with a token from the Backstage Microsoft
 * auth session, the same way `kaap-frontend`'s `ArmApiClient` does - DaaS keeps
 * its own copy rather than depending on another feature plugin's API ref.
 */
export const useAzureSecurityGroups = (): AzureSecurityGroupsApi => {
  const fetchApi = useApi(fetchApiRef);
  const microsoftAuth = useApi(microsoftAuthApiRef);

  const requestGraph = useCallback(
    async (url: string, init: RequestInit = {}) => {
      const token = await microsoftAuth.getAccessToken(GRAPH_GROUP_SCOPE);
      const response = await fetchApi.fetch(url, {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${token}`,
          ConsistencyLevel: 'eventual',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to resolve Azure security groups: ${response.status} ${response.statusText}`,
        );
      }

      return response.json();
    },
    [fetchApi, microsoftAuth],
  );

  const listGroups = useCallback(
    async (path: string, match?: string) => {
      const filter = match
        ? `${SECURITY_GROUP_FILTER} and startswith(displayName, '${escapeODataLiteral(
            match,
          )}')`
        : SECURITY_GROUP_FILTER;
      const query = new URLSearchParams({
        $count: 'true',
        $filter: filter,
        $select: 'id,displayName',
      });

      return toGroups(await requestGraph(`${GRAPH_BASE_URL}/${path}?${query}`));
    },
    [requestGraph],
  );

  const listOwnGroups = useCallback(
    () => listGroups('me/memberOf/microsoft.graph.group'),
    [listGroups],
  );

  const searchGroups = useCallback(
    async (match: string) => {
      const groups = await listGroups('groups', match);
      return groups.filter(group => !isWellKnownGroupName(group.name));
    },
    [listGroups],
  );

  const resolveGroupNames = useCallback(
    async (ids: string[]) => {
      const names = new Map<string, string>();
      if (ids.length === 0) return names;

      const body = await requestGraph(
        `${GRAPH_BASE_URL}/directoryObjects/getByIds`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids, types: ['group'] }),
        },
      );

      for (const group of toGroups(body)) {
        if (group.name) names.set(group.id, group.name);
      }
      return names;
    },
    [requestGraph],
  );

  return useMemo(
    () => ({ listOwnGroups, searchGroups, resolveGroupNames }),
    [listOwnGroups, searchGroups, resolveGroupNames],
  );
};
